import { Container, Graphics } from 'pixi.js';
import type { ParallaxLayers } from '../stage-scene';

export interface StationLight { gfx: Graphics; phase: number; speed: number; rgb?: boolean; }

export interface NeonStationComponents {
    stationContainer: Container;
    armContainerA: Container;
    armContainerB: Container;
    stationLights: StationLight[];
}

/** Draw the space station silhouette with neon wireframe edges. */
export function buildStation(layers: ParallaxLayers, W: number, H: number): NeonStationComponents {
    const cx = W * 0.5;
    const cy = H * 0.3;
    const EDGE = 0x8844ff;
    const EDGE_BRIGHT = 0xbb77ff;
    const GLOW = 0xaa66ff;
    const FILL = 0x050510;

    const stationLights: StationLight[] = [];

    // Main station container — handles wind drift only
    const container = new Container();
    container.pivot.set(cx, cy);
    container.position.set(cx, cy);
    layers.deep.container.addChild(container);

    const hubR = 60;
    const armLength = 280;
    const armWidth = 10;
    const armAngles = [-Math.PI * 0.02, Math.PI * 0.98, -Math.PI * 0.48, Math.PI * 0.52];

    // ── Hub (stationary within station container) ──
    const hubBody = new Graphics();
    const hubWire = new Graphics();

    // Octagonal command module
    const hubPoints: [number, number][] = [];
    for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 - Math.PI / 8;
        hubPoints.push([cx + Math.cos(a) * hubR, cy + Math.sin(a) * hubR]);
    }
    hubBody.moveTo(hubPoints[0][0], hubPoints[0][1]);
    for (let i = 1; i < hubPoints.length; i++) hubBody.lineTo(hubPoints[i][0], hubPoints[i][1]);
    hubBody.closePath();
    hubBody.fill({ color: FILL, alpha: 0.9 });
    hubWire.moveTo(hubPoints[0][0], hubPoints[0][1]);
    for (let i = 1; i < hubPoints.length; i++) hubWire.lineTo(hubPoints[i][0], hubPoints[i][1]);
    hubWire.closePath();
    hubWire.stroke({ color: EDGE, alpha: 0.7, width: 2 });

    // Inner ring — overlaps hub → filled neon glow
    const innerR = 35;
    hubBody.circle(cx, cy, innerR);
    hubBody.fill({ color: GLOW, alpha: 0.15 });
    hubWire.circle(cx, cy, innerR);
    hubWire.stroke({ color: EDGE_BRIGHT, alpha: 0.5, width: 1.5 });

    // Antenna spire
    const antennaTop = cy - 160;
    hubBody.moveTo(cx - 3, cy - hubR);
    hubBody.lineTo(cx - 2, antennaTop);
    hubBody.lineTo(cx + 2, antennaTop);
    hubBody.lineTo(cx + 3, cy - hubR);
    hubBody.closePath();
    hubBody.fill({ color: FILL, alpha: 0.8 });
    hubWire.moveTo(cx - 2, cy - hubR);
    hubWire.lineTo(cx - 1, antennaTop);
    hubWire.stroke({ color: EDGE_BRIGHT, alpha: 0.5, width: 1 });
    hubWire.moveTo(cx + 2, cy - hubR);
    hubWire.lineTo(cx + 1, antennaTop);
    hubWire.stroke({ color: EDGE_BRIGHT, alpha: 0.5, width: 1 });

    // Antenna dish — overlaps antenna → filled neon glow
    hubBody.moveTo(cx - 12, antennaTop + 5);
    hubBody.quadraticCurveTo(cx, antennaTop - 12, cx + 12, antennaTop + 5);
    hubBody.closePath();
    hubBody.fill({ color: GLOW, alpha: 0.2 });
    hubWire.moveTo(cx - 12, antennaTop + 5);
    hubWire.quadraticCurveTo(cx, antennaTop - 12, cx + 12, antennaTop + 5);
    hubWire.stroke({ color: EDGE_BRIGHT, alpha: 0.6, width: 1.5 });

    // Docking bay
    const dockY = cy + hubR;
    hubBody.moveTo(cx - 25, dockY);
    hubBody.lineTo(cx - 20, dockY + 40);
    hubBody.lineTo(cx + 20, dockY + 40);
    hubBody.lineTo(cx + 25, dockY);
    hubBody.closePath();
    hubBody.fill({ color: FILL, alpha: 0.85 });
    hubWire.moveTo(cx - 25, dockY);
    hubWire.lineTo(cx - 20, dockY + 40);
    hubWire.lineTo(cx + 20, dockY + 40);
    hubWire.lineTo(cx + 25, dockY);
    hubWire.stroke({ color: EDGE, alpha: 0.5, width: 1.5 });

    container.addChild(hubBody);
    container.addChild(hubWire);

    // Hub lights (stationary) — purple structural
    const hubLightPositions: [number, number][] = [
        [cx, cy], [cx, antennaTop], [cx - 20, dockY + 40], [cx + 20, dockY + 40],
    ];
    for (const [lx, ly] of hubLightPositions) {
        const gfx = new Graphics();
        gfx.circle(lx, ly, 2.5);
        gfx.fill({ color: EDGE_BRIGHT, alpha: 0.8 });
        container.addChild(gfx);
        stationLights.push({ gfx, phase: Math.random() * Math.PI * 2, speed: 1.5 + Math.random() * 2 });
    }

    // RGB neon accent lights on the hub — ring of colored dots around inner ring
    const RGB_COLORS = [0xff4466, 0x44ff88, 0x4488ff, 0xff44dd, 0x44ffff, 0xffaa22];
    for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        const r = innerR + 8;
        const lx = cx + Math.cos(a) * r;
        const ly = cy + Math.sin(a) * r;
        const color = RGB_COLORS[i % RGB_COLORS.length];
        const gfx = new Graphics();
        gfx.circle(lx, ly, 2);
        gfx.fill({ color, alpha: 0.9 });
        gfx.circle(lx, ly, 6);
        gfx.fill({ color, alpha: 0.12 });
        container.addChild(gfx);
        stationLights.push({ gfx, phase: i * 0.8, speed: 1.2 + Math.random() * 1.5, rgb: true });
    }
    // Antenna spine accent lights
    for (let i = 1; i <= 3; i++) {
        const t = i / 4;
        const color = RGB_COLORS[(i + 2) % RGB_COLORS.length];
        const gfx = new Graphics();
        const ay = (cy - hubR) + (antennaTop - (cy - hubR)) * t;
        gfx.circle(cx, ay, 1.8);
        gfx.fill({ color, alpha: 0.9 });
        gfx.circle(cx, ay, 5);
        gfx.fill({ color, alpha: 0.1 });
        container.addChild(gfx);
        stationLights.push({ gfx, phase: i * 1.2, speed: 2 + Math.random(), rgb: true });
    }

    // ── Two long arms — each passes through the hub ──
    // Arm A: horizontal pair (left + right) with solar panels
    // Arm B: diagonal pair (upper-left + lower-right) with habitat pods
    const armPairs: { angles: number[]; hasPanels: boolean }[] = [
        { angles: [armAngles[0], armAngles[1]], hasPanels: true },
        { angles: [armAngles[2], armAngles[3]], hasPanels: false },
    ];

    let armContainerA: Container = new Container();
    let armContainerB: Container = new Container();

    for (let ai = 0; ai < armPairs.length; ai++) {
        const pair = armPairs[ai];
        const armC = new Container();
        armC.pivot.set(cx, cy);
        armC.position.set(cx, cy);
        container.addChild(armC);
        if (ai === 0) armContainerA = armC;
        else armContainerB = armC;

        const armBody = new Graphics();
        const armWire = new Graphics();

        for (const angle of pair.angles) {
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            const perpX = -sin * armWidth * 0.5;
            const perpY = cos * armWidth * 0.5;
            const startDist = hubR - 5;
            const sx = cx + cos * startDist;
            const sy = cy + sin * startDist;
            const ex = cx + cos * (startDist + armLength);
            const ey = cy + sin * (startDist + armLength);

            // Truss body
            armBody.moveTo(sx + perpX, sy + perpY);
            armBody.lineTo(ex + perpX, ey + perpY);
            armBody.lineTo(ex - perpX, ey - perpY);
            armBody.lineTo(sx - perpX, sy - perpY);
            armBody.closePath();
            armBody.fill({ color: FILL, alpha: 0.85 });

            // Truss wireframe edges
            armWire.moveTo(sx + perpX, sy + perpY);
            armWire.lineTo(ex + perpX, ey + perpY);
            armWire.stroke({ color: EDGE, alpha: 0.6, width: 1.5 });
            armWire.moveTo(sx - perpX, sy - perpY);
            armWire.lineTo(ex - perpX, ey - perpY);
            armWire.stroke({ color: EDGE, alpha: 0.6, width: 1.5 });

            // Cross-bracing — overlaps truss → filled neon glow
            const segments = Math.floor(armLength / 40);
            for (let i = 1; i < segments; i++) {
                const t = i / segments;
                const mx = sx + (ex - sx) * t;
                const my = sy + (ey - sy) * t;
                // Filled glow stripe
                armBody.moveTo(mx + perpX * 0.9, my + perpY * 0.9);
                armBody.lineTo(mx - perpX * 0.9, my - perpY * 0.9);
                armBody.lineTo(mx - perpX * 0.9 + cos * 2, my - perpY * 0.9 + sin * 2);
                armBody.lineTo(mx + perpX * 0.9 + cos * 2, my + perpY * 0.9 + sin * 2);
                armBody.closePath();
                armBody.fill({ color: GLOW, alpha: 0.2 });
                // Wireframe
                armWire.moveTo(mx + perpX, my + perpY);
                armWire.lineTo(mx - perpX, my - perpY);
                armWire.stroke({ color: EDGE_BRIGHT, alpha: 0.35, width: 1 });
            }

            // Arm tip light
            const tipGfx = new Graphics();
            tipGfx.circle(ex, ey, 2.5);
            tipGfx.fill({ color: EDGE_BRIGHT, alpha: 0.8 });
            armC.addChild(tipGfx);
            stationLights.push({ gfx: tipGfx, phase: Math.random() * Math.PI * 2, speed: 1.5 + Math.random() * 2 });

            // RGB neon accent lights along the truss
            const RGB_ARM = [0xff4466, 0x44ff88, 0x4488ff, 0xff44dd, 0x44ffff, 0xffaa22];
            const neonCount = 5;
            for (let n = 0; n < neonCount; n++) {
                const t = (n + 1) / (neonCount + 1);
                const nx = sx + (ex - sx) * t;
                const ny = sy + (ey - sy) * t;
                // Alternate sides of the truss
                const side = n % 2 === 0 ? 1.8 : -1.8;
                const lx = nx + perpX * side / (armWidth * 0.5) * 8;
                const ly = ny + perpY * side / (armWidth * 0.5) * 8;
                const color = RGB_ARM[n % RGB_ARM.length];
                const gfx = new Graphics();
                gfx.circle(lx, ly, 1.5);
                gfx.fill({ color, alpha: 0.9 });
                gfx.circle(lx, ly, 5);
                gfx.fill({ color, alpha: 0.1 });
                armC.addChild(gfx);
                stationLights.push({ gfx, phase: n * 1.1 + angle, speed: 1.0 + Math.random() * 2, rgb: true });
            }
        }

        // ── Solar panels (on arm A) ──
        if (pair.hasPanels) {
            for (const angle of pair.angles) {
                const cos = Math.cos(angle);
                const sin = Math.sin(angle);
                const dist = armLength + hubR + 20;
                const pcx = cx + cos * dist;
                const pcy = cy + sin * dist;
                const perpX = -sin;
                const perpY = cos;
                const pw = 30, ph = 120;

                for (const side of [-1, 1]) {
                    const panelCx = pcx + perpX * side * (ph * 0.5 + 8);
                    const panelCy = pcy + perpY * side * (ph * 0.5 + 8);
                    const corners: [number, number][] = [
                        [panelCx - cos * pw * 0.5 - perpX * ph * 0.5, panelCy - sin * pw * 0.5 - perpY * ph * 0.5],
                        [panelCx + cos * pw * 0.5 - perpX * ph * 0.5, panelCy + sin * pw * 0.5 - perpY * ph * 0.5],
                        [panelCx + cos * pw * 0.5 + perpX * ph * 0.5, panelCy + sin * pw * 0.5 + perpY * ph * 0.5],
                        [panelCx - cos * pw * 0.5 + perpX * ph * 0.5, panelCy - sin * pw * 0.5 + perpY * ph * 0.5],
                    ];
                    armBody.moveTo(corners[0][0], corners[0][1]);
                    for (let i = 1; i < 4; i++) armBody.lineTo(corners[i][0], corners[i][1]);
                    armBody.closePath();
                    armBody.fill({ color: FILL, alpha: 0.85 });
                    armWire.moveTo(corners[0][0], corners[0][1]);
                    for (let i = 1; i < 4; i++) armWire.lineTo(corners[i][0], corners[i][1]);
                    armWire.closePath();
                    armWire.stroke({ color: EDGE, alpha: 0.5, width: 1.5 });

                    // Grid lines — overlaps panel → filled neon glow
                    for (let g = 1; g <= 3; g++) {
                        const t = g / 4;
                        const lx1 = corners[0][0] + (corners[3][0] - corners[0][0]) * t;
                        const ly1 = corners[0][1] + (corners[3][1] - corners[0][1]) * t;
                        const lx2 = corners[1][0] + (corners[2][0] - corners[1][0]) * t;
                        const ly2 = corners[1][1] + (corners[2][1] - corners[1][1]) * t;
                        // Filled glow stripe
                        const nx = perpX * 1.5, ny = perpY * 1.5;
                        armBody.moveTo(lx1, ly1);
                        armBody.lineTo(lx2, ly2);
                        armBody.lineTo(lx2 + nx, ly2 + ny);
                        armBody.lineTo(lx1 + nx, ly1 + ny);
                        armBody.closePath();
                        armBody.fill({ color: GLOW, alpha: 0.15 });
                        armWire.moveTo(lx1, ly1);
                        armWire.lineTo(lx2, ly2);
                        armWire.stroke({ color: EDGE_BRIGHT, alpha: 0.3, width: 1 });
                    }
                }
            }
        }

        // ── Habitat pods (on arm B) ──
        if (!pair.hasPanels) {
            for (const angle of pair.angles) {
                const cos = Math.cos(angle);
                const sin = Math.sin(angle);
                const podDist = hubR + armLength * 0.7;
                const podCx = cx + cos * podDist;
                const podCy = cy + sin * podDist;
                const podR = 28;

                const podPts: [number, number][] = [];
                for (let i = 0; i < 6; i++) {
                    const a = (i / 6) * Math.PI * 2;
                    podPts.push([podCx + Math.cos(a) * podR, podCy + Math.sin(a) * podR]);
                }
                // Pod silhouette
                armBody.moveTo(podPts[0][0], podPts[0][1]);
                for (let i = 1; i < podPts.length; i++) armBody.lineTo(podPts[i][0], podPts[i][1]);
                armBody.closePath();
                armBody.fill({ color: FILL, alpha: 0.9 });
                // Pod wireframe
                armWire.moveTo(podPts[0][0], podPts[0][1]);
                for (let i = 1; i < podPts.length; i++) armWire.lineTo(podPts[i][0], podPts[i][1]);
                armWire.closePath();
                armWire.stroke({ color: EDGE_BRIGHT, alpha: 0.6, width: 1.5 });
                // Inner glow — overlaps pod body
                armBody.circle(podCx, podCy, podR * 0.55);
                armBody.fill({ color: GLOW, alpha: 0.15 });
                armWire.circle(podCx, podCy, podR * 0.55);
                armWire.stroke({ color: EDGE_BRIGHT, alpha: 0.3, width: 1 });

                // Pod light
                const podGfx = new Graphics();
                podGfx.circle(podCx, podCy, 2.5);
                podGfx.fill({ color: EDGE_BRIGHT, alpha: 0.8 });
                armC.addChild(podGfx);
                stationLights.push({ gfx: podGfx, phase: Math.random() * Math.PI * 2, speed: 1.5 + Math.random() * 2 });
            }
        }

        armC.addChild(armBody);
        armC.addChild(armWire);
    }

    return {
        stationContainer: container,
        armContainerA,
        armContainerB,
        stationLights
    };
}
